import { LifiFetchError, fetchLifiQuote, lifiAddressOverride } from '@/domain/lifi/fetchLifiQuote'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { BaseUnitNumber } from '@/domain/types/NumericValues'
import { toBigInt } from '@/utils/bigNumber'
import { track } from '@/features/analytics'
import { classifyError } from '@/features/analytics/classifyError'
import { UseQueryResult, queryOptions, useQuery } from '@tanstack/react-query'
import { Address } from 'viem'
import { useEffect, useRef } from 'react'
import { GetPriceError, GetPriceResult, SwapError, adaptLifiQuoteToGetPriceResult } from './types'

const REFRESH_INTERVAL = 20000

interface GetPriceParams {
  inputToken?: CheckedAddress
  outputToken?: CheckedAddress
  account?: Address
  inputAmount?: BaseUnitNumber
  outputAmount?: BaseUnitNumber
  slippage?: number
  enabled?: boolean
  resetErrors: () => void
  refetchInterval?: number
  refetchOnWindowFocus?: boolean
}

export function getPrice({
  inputToken,
  outputToken,
  account,
  inputAmount,
  outputAmount,
  slippage, // Slippage in basis points (e.g., 50 = 0.5%)
  resetErrors,
}: GetPriceParams) {
  const hasInputAmount = Boolean(inputAmount?.gt(0))
  const hasOutputAmount = Boolean(outputAmount?.gt(0))

  return queryOptions<GetPriceResult | GetPriceError | null>({
    queryKey: priceQueryKey({ inputToken, outputToken, account, inputAmount, outputAmount, slippage }),
    queryFn: async () => {
      if (
        !inputToken ||
        !outputToken ||
        !account ||
        (!hasInputAmount && !hasOutputAmount) ||
        inputToken === outputToken
      ) {
        return null
      }

      resetErrors()

      // LiFi only supports EXACT_IN mode for quotes
      if (!hasInputAmount) {
        // For EXACT_OUT, we'd need to use a different endpoint or estimate
        // For now, return null as LiFi quote endpoint doesn't support outputAmount
        return null
      }

      try {
        const fromAmountBigInt = toBigInt(inputAmount!)

        // Don't call API if amount is 0 or negative
        if (fromAmountBigInt <= 0n) {
          return null
        }

        const lifiQuote = await fetchLifiQuote({
          fromToken: lifiAddressOverride(inputToken),
          toToken: lifiAddressOverride(outputToken),
          fromAmount: fromAmountBigInt.toString(),
          fromAddress: account,
          slippageBps: slippage ?? 50, // Default 0.5% = 50 bps
        })

        // Validate quote has a positive output amount
        const toAmount = BigInt(lifiQuote.estimate?.toAmount ?? '0')
        if (toAmount <= 0n) {
          return null
        }

        // Convert LiFi response to existing GetPriceResult format
        // Pass original token addresses for validation compatibility with hypeAddressOverride
        return adaptLifiQuoteToGetPriceResult(lifiQuote, account, inputToken, outputToken)
      } catch (error) {
        console.error('LiFi quote error:', error)

        if (error instanceof LifiFetchError) {
          return {
            statusCode: error.status,
            error: error.message,
          } as GetPriceError
        }

        if (error instanceof SwapError) {
          return {
            statusCode: 400,
            error: error.message,
          } as GetPriceError
        }

        return {
          statusCode: 500,
          error: error instanceof Error ? error.message : 'Unknown error fetching quote',
        } as GetPriceError
      }
    },
    enabled: Boolean(inputToken && outputToken && account && hasInputAmount),
    refetchInterval: REFRESH_INTERVAL,
    staleTime: 5000,
    gcTime: Number.POSITIVE_INFINITY,
    retry: 2,
    retryDelay: 1000,
  })
}

type PriceQueryKeyParams = Omit<GetPriceParams, 'resetErrors'>

export function priceQueryKey({
  inputToken,
  outputToken,
  account,
  inputAmount,
  outputAmount,
  slippage,
}: PriceQueryKeyParams): unknown[] {
  return [...getPriceQueryKeyPrefix({ inputToken, outputToken, account, inputAmount, outputAmount, slippage })]
}

function getPriceQueryKeyPrefix({
  inputToken,
  outputToken,
  account,
  inputAmount,
  outputAmount,
  slippage,
}: PriceQueryKeyParams): unknown[] {
  return ['lifi-price', inputToken, outputToken, account, inputAmount, outputAmount, slippage]
}

export type UseGetPriceResult = UseQueryResult & { price?: GetPriceResult | GetPriceError | null }

export function useGetPrice({
  inputToken,
  outputToken,
  account,
  inputAmount,
  outputAmount,
  enabled,
  slippage,
  resetErrors,
  refetchInterval,
  refetchOnWindowFocus,
}: GetPriceParams): UseGetPriceResult {
  const response = useQuery({
    ...getPrice({
      inputToken,
      outputToken,
      account,
      inputAmount,
      outputAmount,
      slippage,
      resetErrors,
    }),
    enabled,
    ...(refetchInterval !== undefined ? { refetchInterval } : {}),
    ...(refetchOnWindowFocus !== undefined ? { refetchOnWindowFocus } : {}),
  })

  const lastQuoteKeyRef = useRef<string>('')
  const lastErrorKeyRef = useRef<string>('')

  useEffect(() => {
    const data = response.data
    if (!data) return

    if ('result' in data) {
      const quoteKey = `${inputToken}_${outputToken}_${inputAmount?.toFixed()}`
      if (quoteKey === lastQuoteKeyRef.current) return
      lastQuoteKeyRef.current = quoteKey

      track('swap_quote_received', {
        input_token: String(inputToken ?? ''),
        output_token: String(outputToken ?? ''),
        input_amount: data.result.inputAmount,
        output_amount: data.result.outputAmount,
        slippage_setting: (slippage ?? 50) / 100,
      })
    } else if ('error' in data) {
      const errorKey = `${inputToken}_${outputToken}_${inputAmount?.toFixed()}_${data.error}`
      if (errorKey === lastErrorKeyRef.current) return
      lastErrorKeyRef.current = errorKey

      track('swap_quote_failed', {
        input_token: String(inputToken ?? ''),
        output_token: String(outputToken ?? ''),
        input_amount: inputAmount?.toFixed() ?? '0',
        error_type: classifyError({ message: data.error }),
        error_message: data.error,
      })
    }
  }, [
    response.data,
    inputToken,
    outputToken,
    inputAmount,
    slippage,
  ])

  return {
    ...response,
    price: response.data,
  }
}
